import { FC, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon, InfoCircle } from '../../../../../assets/icons/index';
import { Tooltip } from '../../../../../components/tooltip/index';
import { useLocale } from '../../../../../hooks/index';
import { toMathRound } from '../../../../../utils/to-math-round.util';
import { toNumber } from '../../../../../utils/to-number.util';
import {
  formatScenarioCapTableCellValue,
  isCapTableEmptyDisplayValue,
} from '../../format-scenario-cap-table-cell-value.util';
import {
  CapTableTab,
  Column,
  ColumnVariant,
  ShareClassColumns,
  shareClassesViewByColumns,
  StakeholderColumns,
  StakeholdersViewBy,
  stakeholdersViewByColumns,
} from '../fundraising.types';

type TableRowProps = {
  className?: string;
  handleExpandRow?: React.Dispatch<React.SetStateAction<boolean>>;
  isExpanded?: boolean;
  isExpandedRow: boolean;
  isPool?: boolean;
  selectedViewBy?: 'all' | StakeholdersViewBy;
  showToolTip?: boolean;
  tdClassName?: string;
  trClassName?: string;
  viewBy?: Record<StakeholdersViewBy, boolean>;
  visibleColumns: Column[];
} & (
  | {
      fields: Record<
        ShareClassColumns,
        {
          change: null | number | number[] | string | string[];
          value: number | number[] | string | string[];
        } | null
      >;
      tab: CapTableTab.BY_SHARE_CLASS;
    }
  | {
      fields: Record<
        StakeholderColumns,
        {
          change: null | number | number[] | string | string[];
          value: number | number[] | string | string[];
        } | null
      >;
      tab: CapTableTab.BY_STAKEHOLDER;
    }
);
const allColumnsMap = Object.values(Column);

const allColumnFields = allColumnsMap.reduce(
  (acc, curr) => ({ ...acc, [curr]: null }),
  {},
) as Record<Column, null>;

export const TableRow: FC<TableRowProps> = ({
  fields,
  className,
  trClassName,
  visibleColumns = [],
  viewBy,
  handleExpandRow,
  isExpanded,
  isExpandedRow,
  tab,
  selectedViewBy = 'all',
  tdClassName,
  showToolTip,
  isPool = false,
}) => {
  const viewByAll = selectedViewBy === 'all';
  const { messagesLocale } = useLocale();
  const basicIsVisibleChecks = useMemo(
    () =>
      allColumnsMap.reduce(
        (acc, curr) => ({ ...acc, [curr]: visibleColumns.includes(curr) }),
        {},
      ) as Record<Column, boolean>,
    [visibleColumns],
  );

  const isVisibleChecks: Record<ColumnVariant, boolean> = useMemo(
    () => ({
      ...basicIsVisibleChecks,
      [Column.NAME]: visibleColumns.includes(Column.NAME) && viewByAll,
      [Column.STAKEHOLDER_NAME]:
        visibleColumns.includes(Column.STAKEHOLDER_NAME) &&
        !viewByAll &&
        (viewBy?.stakeholders || false),
      [Column.TYPE]: visibleColumns.includes(Column.TYPE) && (!viewByAll || isPool),
    }),
    [basicIsVisibleChecks, isPool, viewBy?.stakeholders, viewByAll, visibleColumns],
  );

  const fieldsMap = useMemo(
    () =>
      tab === CapTableTab.BY_SHARE_CLASS
        ? shareClassesViewByColumns
        : stakeholdersViewByColumns[selectedViewBy],
    [selectedViewBy, tab],
  );

  const allFields = useMemo(() => ({ ...allColumnFields, ...fields }), [fields]);

  return (
    <>
      <tr className={twMerge(isExpanded && 'shadow-[inset_2px_0px_0px_0px_#2565C8]', trClassName)}>
        {viewByAll || isPool ? (
          <td
            className={twMerge(
              'w-12 text-center shadow-[inset_0px_-1px_0px_0px_#F2F4F7]',
              !isExpandedRow && 'cursor-pointer',
              isExpanded && 'bg-brand-50 shadow-[inset_2px_0px_0px_0px_#2565C8]',
              className,
            )}
            onClick={() => !isExpandedRow && handleExpandRow?.((prev) => !prev)}
          >
            <span className="centered flex w-12 shrink-0 text-sm font-[450] text-gray-700">
              {!isExpandedRow && (
                <ChevronDownIcon
                  className={twMerge('transition-all', isExpanded && 'rotate-180')}
                  iconColor={isExpanded ? '#2565C8' : '#344054'}
                />
              )}
            </span>
          </td>
        ) : (
          <td
            className={twMerge(
              'w-12 text-center shadow-[inset_0px_-1px_0px_0px_#F2F4F7]',
              !isExpandedRow && 'cursor-pointer',
              isExpanded && 'bg-brand-50 shadow-[inset_2px_0px_0px_0px_#2565C8]',
              className,
            )}
          ></td>
        )}

        {fieldsMap.map(
          (field, i) =>
            isVisibleChecks[field] &&
            (Array.isArray(allFields[field]?.value) ? (
              allFields[field]?.value?.map((filed, i) => {
                const displayValue = formatScenarioCapTableCellValue(field, filed);

                return (
                  <td
                    className="px-4 py-6 shadow-[inset_0px_-1px_0px_0px_#F2F4F7]"
                    key={`${field}_${i}_${tab}`}
                  >
                    <div className="flex max-w-[360px] gap-6 overflow-hidden">
                      <span
                        className={twMerge(
                          'w-[80px] text-sm font-[450] text-gray-700',
                          (!allFields[field] || isCapTableEmptyDisplayValue(displayValue)) &&
                            'text-gray-300',
                          tdClassName,
                        )}
                      >
                        {displayValue}
                      </span>
                      {Array.isArray(allFields[field]?.change) &&
                      allFields[field]?.change[i] !== null ? (
                        <span
                          className={twMerge(
                            'w-[80px] rounded-2xl px-[6px] py-[2px] text-xs font-medium',
                            typeof allFields[field]?.change[i] === 'string' && 'text-gray-400',
                            typeof allFields[field]?.change[i] === 'number' &&
                              (allFields[field]?.change[i] > 0
                                ? 'bg-forest-50 text-forest-600'
                                : allFields[field]?.change[i] < 0
                                  ? 'bg-fireside-50 text-fireside-600'
                                  : 'bg-gray-50 text-gray-400'),
                          )}
                        >
                          {typeof allFields[field]?.change[i] === 'number'
                            ? allFields[field]?.change[i] > 0
                              ? `+${toMathRound(toNumber(allFields[field]?.change[i]) * 100).toLocaleString('en-US')}%`
                              : allFields[field]?.change[i] < 0
                                ? `${toMathRound(toNumber(allFields[field]?.change[i]) * 100)}%`
                                : `00`
                            : allFields[field]?.change[i]}
                        </span>
                      ) : (
                        <>
                          {field !== Column.TYPE &&
                            field !== Column.NAME &&
                            field !== Column.STAKEHOLDER_NAME && <span className="w-[80px]" />}
                        </>
                      )}
                    </div>
                  </td>
                );
              })
            ) : (
              <td
                className="px-4 py-6 shadow-[inset_0px_-1px_0px_0px_#F2F4F7]"
                key={`${field}_${i}_${tab}`}
              >
                <div
                  className={twMerge(
                    'flex max-w-[360px] items-center overflow-hidden',
                    showToolTip && (field === Column.NAME || field === Column.STAKEHOLDER_NAME)
                      ? 'gap-4'
                      : 'gap-6',
                  )}
                >
                  <span
                    className={twMerge(
                      'truncate text-ellipsis whitespace-nowrap text-sm font-[450] text-gray-700',
                      isCapTableEmptyDisplayValue(
                        formatScenarioCapTableCellValue(field, allFields[field]?.value),
                      ) && 'w-[80px] text-gray-300',
                      (typeof allFields[field]?.value === 'number' ||
                        !isNaN(Number(allFields[field]?.value))) &&
                        'w-[80px]',
                      tdClassName,
                    )}
                  >
                    {formatScenarioCapTableCellValue(field, allFields[field]?.value)}
                  </span>
                  {showToolTip && (field === Column.NAME || field === Column.STAKEHOLDER_NAME) && (
                    <Tooltip content={messagesLocale['ANTI_DILUTION_TOOLTIP']}>
                      <InfoCircle className="size-4 cursor-pointer" iconColor="#344054" />
                    </Tooltip>
                  )}
                  {allFields[field]?.change !== null && field !== Column.AVERAGE_SHARE_PRICE ? (
                    <span
                      className={twMerge(
                        'flex w-[80px] justify-center rounded-2xl px-[6px] py-[2px] text-xs font-medium',
                        typeof allFields[field]?.change === 'string' && 'text-gray-400',
                        typeof allFields[field]?.change === 'number' &&
                          (allFields[field]?.change > 0
                            ? 'bg-forest-50 text-forest-600'
                            : allFields[field]?.change < 0
                              ? 'bg-fireside-50 text-fireside-600'
                              : 'bg-gray-50 text-gray-400'),
                      )}
                    >
                      {typeof allFields[field]?.change === 'number'
                        ? allFields[field]?.change > 0
                          ? `+${toMathRound(toNumber(allFields[field]?.change) * 100).toLocaleString('en-US')}%`
                          : allFields[field]?.change < 0
                            ? `${toMathRound(toNumber(allFields[field]?.change) * 100)}%`
                            : `00`
                        : allFields[field]?.change}
                    </span>
                  ) : (
                    <>
                      {field !== Column.TYPE &&
                        field !== Column.NAME &&
                        field !== Column.STAKEHOLDER_NAME && <span className="w-[80px]" />}
                    </>
                  )}
                </div>
              </td>
            )),
        )}
      </tr>
    </>
  );
};
